import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import {
  AttachFileOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  ChatBubbleOutline,
  CloseOutlined,
  ConfirmationNumberOutlined,
  CurrencyRupeeOutlined,
  DescriptionOutlined,
  EditOutlined,
  EventRepeatOutlined,
  HighlightOff,
  SourceOutlined,
  TagOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { LoaderWithoutHeight } from "components/Loader";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import {
  fetchPayInputClaim,
  fetchPayInputEmployees,
  openClaimAttachment,
  resolveAttachmentUrl,
} from "pages/PayRoll/Shared/reimbursementApi";
import { ViewActionFooter } from "../Shared/ViewActionFooter";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { DateField, SelectField, TextField } from "../Shared/fieldInputs";
import { formatDate, money } from "./reimbursementPayUi";

const employeeCard = (employee) => ({
  image_url: employee.photo,
  employee_id: employee.code,
  user_name: employee.name,
  department_name: employee.department,
  designation: employee.designation,
});

// RequestDetailHeader's status chip knows Pending / Approved / Rejected
// colours; Ready shows as Approved-green, Processed as Completed.
const HEADER_STATUS = { PENDING: "Pending", READY: "Approved", PROCESSED: "Completed", REJECTED: "Rejected" };

const timeText = (at) => (at ? moment(at).format("DD MMM YYYY, HH:mm") : "Waiting");

// Timeline step -> one detail row (Submitted, approval stages, payroll).
const timelineField = (step, accent) => {
  const parts = [];
  if (step.step === "STAGE") {
    parts.push(step.by ? `${step.by}` : "Approver not set");
    if (step.status) parts.push(step.status.charAt(0) + step.status.slice(1).toLowerCase());
    if (step.amount != null) parts.push(`₹${money(step.amount)}`);
    if (step.remarks) parts.push(step.remarks);
  } else if (step.step === "PAYROLL") {
    if (step.status) parts.push(step.status.charAt(0) + step.status.slice(1).toLowerCase());
  } else if (step.by) {
    parts.push(step.by);
  }
  return {
    icon: CalendarMonthOutlined,
    label: `${step.stageNo ? `Stage ${step.stageNo} · ` : ""}${step.label || step.step} · ${timeText(step.at)}`,
    value: parts.join(" · ") || "–",
    accent,
    multiline: true,
  };
};

// View: the row at once, then the detail (bills, comments, timeline) from
// GET claims/{id}/. Edit and Cancel only when the server allows them
// (Pending Manual / Import claims, caller with edit). Approval stays in
// Team Workspace.
export const ReimbursementViewPanel = ({ row, domain, companyId, busy, expanded, onToggleExpand, onClose, onEdit, onCancel }) => {
  const [detail, setDetail] = useState({ id: null, data: null, loading: false, error: "" });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!row?.id) return undefined;
    const controller = new AbortController();
    setDetail({ id: row.id, data: null, loading: true, error: "" });
    fetchPayInputClaim({ domain, companyId, id: row.id, signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setDetail({ id: row.id, data, loading: false, error: "" }); })
      .catch((error) => { if (!controller.signal.aborted) setDetail({ id: row.id, data: null, loading: false, error: error.message }); });
    return () => controller.abort();
  }, [row?.id, domain, companyId, retry]);

  if (!row) return null;
  const claim = detail.id === row.id && detail.data ? { ...row, ...detail.data } : row;
  const { indigo, blue, green, orange, purple, pink } = detailAccentPalette;
  const reduced = claim.amountApproved != null && claim.amountApproved !== claim.amount;

  const openBill = async (file) => {
    try {
      await openClaimAttachment(resolveAttachmentUrl(domain, file.url));
    } catch (error) {
      showToast("error", error.message || "Couldn't open the bill.");
    }
  };

  return (
    <GlobalSidePanel
      open
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Reimbursement"
      subtitle={`${claim.requestNo} · ${claim.typeName || "Claim"}`}
      bodySx={panelBodySx}
      headerActions={
        claim.canEdit ? (
          <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
            <span>
              <Mui.IconButton
                size="small"
                aria-label="Edit"
                disabled={busy}
                onClick={() => onEdit(claim)}
                sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </span>
          </Mui.Tooltip>
        ) : null
      }
      footer={
        claim.canCancel ? (
          <ViewActionFooter recordAction={{ label: "Cancel Claim", icon: HighlightOff, onClick: () => onCancel(claim), disabled: busy }} />
        ) : null
      }
    >
      <Mui.Box sx={{ mb: 2 }}>
        <RequestDetailHeader userDetails={employeeCard(claim.employee)} status={HEADER_STATUS[claim.statusCode]} />
      </Mui.Box>
      <Mui.Stack spacing={2}>
        <RequestDetailsSection
          title="CLAIM DETAILS"
          fields={[
            { icon: TagOutlined, label: "Request No", value: claim.requestNo || "–", accent: indigo },
            { icon: CategoryOutlined, label: "Reimbursement Type", value: claim.typeName || "–", accent: pink },
            { icon: CalendarMonthOutlined, label: "Bill Date", value: formatDate(claim.billDate), accent: indigo },
            { icon: ConfirmationNumberOutlined, label: "Bill Number", value: claim.billNumber || "–", accent: purple },
            {
              icon: CurrencyRupeeOutlined,
              label: reduced ? "Amount Approved" : "Amount",
              value: reduced ? `₹${money(claim.amountApproved)} (claimed ₹${money(claim.amount)})` : `₹${money(claim.amount)}`,
              accent: orange,
            },
            { icon: DescriptionOutlined, label: "Description", value: claim.description || "–", accent: blue, multiline: true },
            ...(claim.comments ? [{ icon: ChatBubbleOutline, label: "Comments", value: claim.comments, accent: green, multiline: true }] : []),
          ]}
        />
        <RequestDetailsSection
          title="PAYROLL"
          fields={[
            {
              icon: EventRepeatOutlined,
              label: claim.payrollCycle?.expected ? "Payroll Cycle (expected if approved today)" : "Payroll Cycle",
              value: claim.payrollCycle?.name || "–",
              accent: indigo,
            },
            { icon: SourceOutlined, label: "Source", value: claim.sourceLabel || "–", accent: purple },
            ...(claim.reason ? [{ icon: HighlightOff, label: claim.statusCode === "REJECTED" ? "Reject Reason" : "Reason", value: claim.reason, accent: orange, multiline: true }] : []),
          ]}
        />

        {detail.loading ? <LoaderWithoutHeight /> : null}
        {detail.error ? (
          <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>
            Couldn't load the bills and timeline. {detail.error}
          </Mui.Alert>
        ) : null}

        {claim.attachments?.length ? (
          <RequestDetailsSection
            title="BILLS"
            fields={claim.attachments.map((file) => ({
              icon: AttachFileOutlined,
              label: file.size ? `${Math.max(1, Math.round(file.size / 1024))} KB` : "Bill",
              value: (
                <Mui.Link component="button" type="button" onClick={() => openBill(file)} sx={{ fontWeight: designWeights.semibold, color: designColors.tealDark }}>
                  {file.name}
                </Mui.Link>
              ),
              accent: blue,
            }))}
          />
        ) : null}

        {claim.timeline?.length ? (
          <RequestDetailsSection title="PROGRESS" fields={claim.timeline.map((step) => timelineField(step, green))} />
        ) : null}

        {claim.statusCode === "PENDING" && claim.sourceCode === "MY_WORKSPACE" ? (
          <RequestInfoBanner tone="warning" body="Raised in My Workspace. It is approved in Team Workspace, and only the employee can change it." />
        ) : null}
        {claim.statusCode === "PENDING" && claim.sourceCode !== "MY_WORKSPACE" && !claim.canEdit ? (
          <RequestInfoBanner tone="warning" body="Waiting for approval in Team Workspace. It can no longer be edited here." />
        ) : null}
        {claim.statusCode === "PROCESSED" ? (
          <RequestInfoBanner body="Taken into payroll — read-only. Corrections go through Payroll Adjustments." />
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const AMOUNT = /^\d+(\.\d{1,2})?$/;
const MAX_FILE = 5 * 1024 * 1024;
const FILE_TYPES = /\.(jpe?g|png|pdf)$/i;

// Client checks before the server's (the server returns the same rules,
// plus the monthly limit and duplicate bill).
const validate = (form, type, hasBillFile) => {
  const errors = {};
  if (!form.employeeId) errors.employee_id = "Select the employee.";
  if (!form.typeId) errors.expense_type_id = "Select the reimbursement type.";
  if (!form.billDate) errors.bill_date = "Enter the bill date.";
  else if (moment(form.billDate).isAfter(moment(), "day")) errors.bill_date = "Bill date cannot be in the future.";
  else if (type?.billWithinDays != null && moment().startOf("day").diff(moment(form.billDate), "days") > type.billWithinDays) {
    errors.bill_date = `Bill must be within ${type.billWithinDays} days.`;
  }
  const amount = String(form.amount ?? "").trim();
  if (!amount) errors.amount = "Enter the amount.";
  else if (!/^-?\d+(\.\d+)?$/.test(amount) || Number(amount) <= 0) errors.amount = "Amount must be more than 0.";
  else if (!AMOUNT.test(amount)) errors.amount = "Amount can have at most 2 decimals.";
  else if (type?.limitPerClaim != null && Number(amount) > type.limitPerClaim) errors.amount = `Up to ₹${money(type.limitPerClaim)} per claim for ${type.name}.`;
  if (type?.billNeeded && !String(form.billNumber || "").trim() && !hasBillFile) {
    errors.bill_number = `${type.name} needs a bill number or a bill file.`;
  }
  if (!String(form.description || "").trim()) errors.description = "Enter a description.";
  return errors;
};

// + Add Reimbursement (source Manual) and Edit (Pending Manual / Import
// claims; only changed fields are sent). The payroll cycle is set by the
// server from the Settings' payroll treatment.
export const ReimbursementFormPanel = ({
  claim, domain, companyId, types, billUploadEnabled, approvalRequired, saving, serverErrors, onClose, onSave,
}) => {
  const editing = Boolean(claim);
  const initial = useMemo(() => (claim
    ? {
      employeeId: claim.employee.id,
      typeId: claim.typeId ?? "",
      billDate: claim.billDate || "",
      billNumber: claim.billNumber || "",
      amount: String(claim.amount ?? ""),
      description: claim.description || "",
      comments: claim.comments || "",
    }
    : { employeeId: "", typeId: "", billDate: "", billNumber: "", amount: "", description: "", comments: "" }), [claim]);
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const [employees, setEmployees] = useState({ list: [], loading: !editing, error: "" });
  useEffect(() => {
    if (editing) return undefined;
    const controller = new AbortController();
    fetchPayInputEmployees({ domain, companyId, signal: controller.signal })
      .then((list) => { if (!controller.signal.aborted) setEmployees({ list, loading: false, error: "" }); })
      .catch((error) => { if (!controller.signal.aborted) setEmployees({ list: [], loading: false, error: error.message }); });
    return () => controller.abort();
  }, [editing, domain, companyId]);

  const typeOptions = types.filter((t) => t.active || t.id === claim?.typeId).map((t) => ({ value: t.id, label: t.name }));
  if (claim?.typeId && !typeOptions.some((o) => o.value === claim.typeId)) typeOptions.push({ value: claim.typeId, label: claim.typeName });
  const type = types.find((t) => t.id === form.typeId);
  const hasBillFile = files.length > 0 || Boolean(claim?.hasBill);
  const errors = useMemo(() => validate(form, type, hasBillFile), [form, type, hasBillFile]);
  const shown = { ...(serverErrors || {}), ...(submitted ? errors : {}) };
  const employee = editing ? claim.employee : employees.list.find((e) => e.id === form.employeeId);

  const typeRules = type
    ? [
      type.limitPerClaim != null ? `up to ₹${money(type.limitPerClaim)} a claim` : null,
      type.limitPerMonth != null ? `₹${money(type.limitPerMonth)} a month` : null,
      type.limitPerClaim == null && type.limitPerMonth == null ? "no limit" : null,
      type.billWithinDays != null ? `bill within ${type.billWithinDays} days` : null,
      type.billNeeded ? "bill needed" : null,
    ].filter(Boolean).join(" · ")
    : "";

  const addFiles = (list) => {
    const picked = Array.from(list || []);
    const bad = picked.find((file) => !FILE_TYPES.test(file.name) || file.size > MAX_FILE);
    setFileError(bad ? `${bad.name}: only JPG, PNG or PDF up to 5 MB.` : "");
    setFiles((prev) => [...prev, ...picked.filter((file) => FILE_TYPES.test(file.name) && file.size <= MAX_FILE)]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = () => {
    setSubmitted(true);
    if (Object.keys(errors).length || saving) return;
    if (!editing) { onSave({ values: form, files }); return; }
    const changes = {};
    Object.keys(form).forEach((key) => {
      if (key !== "employeeId" && String(form[key] ?? "") !== String(initial[key] ?? "")) changes[key] = form[key];
    });
    onSave({ changes });
  };

  const err = (key) => shown[key];

  return (
    <GlobalSidePanel
      open
      onClose={() => { if (!saving) onClose(); }}
      title={editing ? "Edit Reimbursement" : "Add Reimbursement"}
      subtitle={editing
        ? `${claim.requestNo} · ${claim.employee.name}`
        : `Enter a claim for one employee. It is saved as ${approvalRequired ? "Pending and goes for approval" : "Ready for payroll"}.`}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={() => { if (!saving) onClose(); }}
          primaryLabel={saving ? "Saving..." : editing ? "Save changes" : "Add reimbursement"}
          primaryProps={{ onClick: save, disabled: saving || (!editing && employees.loading) }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        <FormSection title="Employee">
          <Field label="Employee *" helper="Who the reimbursement is paid to." error={err("employee_id")} wide>
            {editing ? (
              <TextField value={`${claim.employee.name} (${claim.employee.code})`} onChange={() => {}} disabled />
            ) : (
              <SelectField
                value={form.employeeId}
                onChange={(value) => set("employeeId")(value === "" ? "" : Number(value))}
                options={employees.list.map((e) => ({ value: e.id, label: e.code ? `${e.name} (${e.code})` : e.name }))}
                placeholder={employees.loading ? "Loading employees..." : "Select employee"}
                error={err("employee_id")}
                disabled={saving || employees.loading}
              />
            )}
          </Field>
          {employees.error ? <Mui.Alert severity="error" sx={{ gridColumn: "1 / -1" }}>Couldn't load employees. {employees.error}</Mui.Alert> : null}
        </FormSection>
        {employee ? <RequestDetailHeader userDetails={employeeCard(employee)} /> : null}

        <FormSection title="Claim Details">
          <Field label="Reimbursement type *" helper={typeRules || "Types are set in Reimbursement settings."} error={err("expense_type_id")} wide>
            <SelectField
              value={form.typeId}
              onChange={(value) => set("typeId")(value === "" ? "" : Number(value))}
              options={typeOptions}
              placeholder={typeOptions.length ? "Select type" : "No active types — add one in Reimbursement settings"}
              error={err("expense_type_id")}
              disabled={saving}
            />
          </Field>
          <Field label="Bill date *" helper="Date printed on the bill." error={err("bill_date")}>
            <DateField value={form.billDate} onChange={set("billDate")} maxDate={moment().format("YYYY-MM-DD")} error={err("bill_date")} disabled={saving} />
          </Field>
          <Field label={`Bill number${type?.billNeeded ? " *" : ""}`} helper={type?.billNeeded ? "Needed for this type." : "If there is one."} error={err("bill_number")}>
            <TextField value={form.billNumber} onChange={(value) => set("billNumber")(value.slice(0, 50))} placeholder="e.g. INV-1001" error={err("bill_number")} disabled={saving} />
          </Field>
          <Field label="Amount (₹) *" helper="More than 0, at most 2 decimals." error={err("amount")} wide>
            <TextField type="number" value={form.amount} onChange={set("amount")} placeholder="e.g. 640" error={err("amount")} disabled={saving} />
          </Field>
          <Field label="Description *" helper="What the expense was for." error={err("description")} wide>
            <TextField type="multiline" value={form.description} onChange={(value) => set("description")(value.slice(0, 255))} placeholder="e.g. Bus fare to Chennai office" error={err("description")} disabled={saving} />
          </Field>
          <Field label="Comments" helper="Optional." error={err("comments")} wide>
            <TextField type="multiline" value={form.comments} onChange={(value) => set("comments")(value.slice(0, 255))} placeholder="e.g. Return trip" disabled={saving} />
          </Field>
        </FormSection>

        {!editing && billUploadEnabled ? (
          <FormSection title="Bills">
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <SecondaryButton startIcon={<UploadFileOutlined />} disabled={saving} onClick={() => fileRef.current?.click()}>Attach bill</SecondaryButton>
              <input ref={fileRef} type="file" hidden multiple accept=".jpg,.jpeg,.png,.pdf" onChange={(event) => addFiles(event.target.files)} />
              <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>JPG, PNG or PDF, up to 5 MB each.</Mui.Typography>
              {fileError || err("attachments") ? (
                <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.5 }}>{fileError || err("attachments")}</Mui.Typography>
              ) : null}
              {files.length ? (
                <Mui.Stack spacing={0.75} sx={{ mt: 1.25 }}>
                  {files.map((file, index) => (
                    <Mui.Stack key={`${file.name}${index}`} direction="row" alignItems="center" spacing={1} sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: "8px", px: 1.25, py: 0.5 }}>
                      <AttachFileOutlined fontSize="small" sx={{ color: designColors.slate600 }} />
                      <Mui.Typography sx={{ ...designType.tableBodyCell, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</Mui.Typography>
                      <Mui.IconButton size="small" aria-label={`Remove ${file.name}`} disabled={saving} onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}>
                        <CloseOutlined fontSize="small" />
                      </Mui.IconButton>
                    </Mui.Stack>
                  ))}
                </Mui.Stack>
              ) : null}
            </Mui.Box>
          </FormSection>
        ) : null}

        <Mui.Typography sx={designType.captionText}>
          The payroll cycle is set automatically from the payroll treatment in Reimbursement settings{editing ? "." : `, once the claim is ${approvalRequired ? "approved" : "saved"}.`}
        </Mui.Typography>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

